import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Breadcrumb from "../../components/Breadcrumb";
import PageHeader from "../../components/common/PageHeader";
import MasterLayout from "../../masterLayout/MasterLayout";
import AffiliatesServices from "../../services/api/affiliates";

/**
 * Página: Produtos que Gerencio
 *
 * Exibe os produtos onde o usuário logado é gerente de afiliados.
 * Gerentes podem recrutar e gerenciar afiliados para esses produtos.
 */
const ProductsManaged = () => {
  const navigate = useNavigate();
  const [managedProducts, setManagedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getManagedProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await AffiliatesServices.getAffiliatesProducts();

      if (response && response.success) {
        // Filtrar apenas gerentes (role_type === 'manager')
        const onlyManagers = (response.data || []).filter(
          item => item.role_type === 'manager'
        );
        setManagedProducts(onlyManagers);
      } else {
        setError('Resposta inválida da API');
        setManagedProducts([]);
      }
    } catch (error) {
      console.error("Erro ao buscar produtos que gerencio:", error);
      setError(error.message || 'Erro ao carregar produtos que gerencio');
      setManagedProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getManagedProducts();
  }, [getManagedProducts]);

  const handleViewProduct = useCallback((product) => {
    // Usar product_code (UUID) para maior seguranca na URL
    const identifier = product?.product?.product_code || product?.product?.id;
    if (identifier) {
      navigate(`/products/${identifier}`);
    }
  }, [navigate]);

  // Renderizar os cards de produtos gerenciados
  const renderProductCards = useMemo(() => {
    if (managedProducts.length === 0) {
      return (
        <div className="card h-100 p-0 radius-12">
          <div className="card-body p-24">
            <div className="text-center py-5">
              <Icon icon="mdi:account-tie" className="fs-1 text-muted mb-3" />
              <h6 className="text-muted">Nenhum produto gerenciado encontrado</h6>
              <p className="text-muted">
                Você ainda não é gerente de afiliados de nenhum produto.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="mb-0">Produtos que Gerencio ({managedProducts.length})</h6>
          </div>

          <div className="row gy-4">
            {managedProducts.map((item, index) => (
              <div className="col-xxl-3 col-md-6 user-grid-card" key={item.id || index}>
                <div className="position-relative border radius-16 overflow-hidden h-100 bg-white shadow-sm hover-shadow-lg transition-all">
                  {/* Imagem de fundo */}
                  <div
                    className="position-relative bg-light d-flex align-items-center justify-content-center overflow-hidden"
                    style={{ height: "150px" }}
                  >
                    <img
                      src={item.product?.image || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA0MEM2Ni42MjcgNDAgNzIgNDUuMzcyIDcyIDUyQzcyIDU4LjYyNyA2Ni42MjcgNjQgNjAgNjRDNzMuMzcyIDY0IDU4LjYyNyA2NCA1MiA2NEM1MiA1OC42MjcgNTIuMzcyIDUyIDUyIDUyQzUyIDQ1LjM3MiA1Ny4zNzIgNDAgNjAgNDBaIiBmaWxsPSIjOUNBM0FGIi8+Cjx0ZXh0IHg9IjYwIiB5PSI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZCNzI4MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIj5Qcm9kdXRvPC90ZXh0Pgo8L3N2Zz4K"}
                      alt={item.product?.name || 'Produto'}
                      className="w-100 h-100 object-fit-cover"
                      style={{ filter: 'brightness(0.9)' }}
                      onError={(e) => {
                        e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA0MEM2Ni42MjcgNDAgNzIgNDUuMzcyIDcyIDUyQzcyIDU4LjYyNyA2Ni42MjcgNjQgNjAgNjRDNzMuMzcyIDY0IDU4LjYyNyA2NCA1MiA2NEM1MiA1OC42MjcgNTIuMzcyIDUyIDUyIDUyQzUyIDQ1LjM3MiA1Ny4zNzIgNDAgNjAgNDBaIiBmaWxsPSIjOUNBM0FGIi8+Cjx0ZXh0IHg9IjYwIiB5PSI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZCNzI4MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIj5Qcm9kdXRvPC90ZXh0Pgo8L3N2Zz4K";
                      }}
                    />

                    {/* Badge de Gerente */}
                    <div className="position-absolute top-12 end-12">
                      <span className="badge bg-info text-white px-8 py-4 text-xs fw-medium radius-6">
                        <Icon icon="mdi:account-tie" className="me-1" />
                        Gerente
                      </span>
                    </div>
                  </div>

                  {/* Conteudo do card */}
                  <div className="p-16">
                    {/* Nome do produto */}
                    <div className="mb-12">
                      <h6 className="text-md mb-6 fw-semibold line-height-1-3" title={item.product?.name}>
                        {item.product?.name || 'Produto sem nome'}
                      </h6>

                      {/* Codigo do produto */}
                      <div className="d-flex align-items-center gap-2 mb-8">
                        <Icon icon="solar:hashtag-linear" className="text-xs text-secondary-light" />
                        <span className="text-xs text-secondary-light fw-medium font-monospace">
                          {item.product?.product_code || item.product?.id || 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Informacoes do produto */}
                    <div className="mb-16">
                      <div className="d-flex align-items-center gap-2 mb-8">
                        <Icon icon="mdi:account" className="text-xs text-secondary-light" />
                        <span className="text-xs text-secondary-light">
                          Produtor: {item.product?.producer_name || 'N/A'}
                        </span>
                      </div>

                      {/* Sua comissao como gerente */}
                      <div className="d-flex align-items-center gap-2 mb-8">
                        <Icon icon="mdi:percent" className="text-xs text-secondary-light" />
                        <span className="text-xs text-secondary-light">
                          Sua comissao: {item.commission_rate || 0}%
                        </span>
                      </div>

                      {/* Data de cadastro */}
                      <div className="d-flex align-items-center gap-2 mb-8">
                        <Icon icon="mdi:calendar" className="text-xs text-secondary-light" />
                        <span className="text-xs text-secondary-light">
                          Gerente desde: {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>

                    {/* Botao de acao */}
                    <button
                      onClick={() => handleViewProduct(item)}
                      className="bg-info-50 text-info-600 bg-hover-info-600 hover-text-white p-10 text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center justify-content-center mt-16 fw-medium gap-2 w-100 transition-all"
                    >
                      <Icon icon="mdi:account-group" className="icon text-xl line-height-1" />
                      Gerenciar Afiliados
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }, [managedProducts, handleViewProduct]);

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb title="Produtos que Gerencio" />
        <PageHeader
          icon="mdi:account-tie"
          title="Produtos que Gerencio"
          subtitle="Gerencie seus afiliados nos produtos onde voce e gerente"
        />
        <div className="text-center py-5">
          <div className="spinner-border text-info mb-3" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="text-muted">Carregando produtos que gerencio...</p>
        </div>
      </MasterLayout>
    );
  }

  if (error) {
    return (
      <MasterLayout>
        <Breadcrumb title="Produtos que Gerencio" />
        <PageHeader
          icon="mdi:account-tie"
          title="Produtos que Gerencio"
          subtitle="Gerencie seus afiliados nos produtos onde voce e gerente"
        />
        <div className="alert alert-danger" role="alert">
          <h6 className="alert-heading">Erro ao carregar produtos que gerencio</h6>
          <p className="mb-0">{error}</p>
          <button
            className="btn btn-outline-danger btn-sm mt-2"
            onClick={getManagedProducts}
          >
            Tentar novamente
          </button>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title="Produtos que Gerencio" />
      <PageHeader
        icon="mdi:account-tie"
        title="Produtos que Gerencio"
        subtitle="Gerencie seus afiliados nos produtos onde voce e gerente"
      />
      {renderProductCards}
    </MasterLayout>
  );
};

export default ProductsManaged;
